import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'

const RawMaterialFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Get current user and check role
  const currentUser = AuthService.getCurrentUser()
  const userRole = currentUser?.role?.toLowerCase()
  const isBranchRestricted = ['manager', 'warehouse'].includes(userRole)
  const userBranchId = currentUser?.branchId || currentUser?.branch?._id || currentUser?.branch
  
  // Auto-set branch filter for restricted users on mount
  useEffect(() => {
    if (isBranchRestricted && userBranchId && !filters?.branch) {
      console.log('🔒 Raw Material Report: Auto-setting branch for restricted user:', userBranchId)
      onFiltersChange({ ...filters, branch: userBranchId })
    }
  }, [isBranchRestricted, userBranchId])
  
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoading(true)
        const response = await BranchService.getAllBranches()
        
        let branchesData = []
        if (response && response.success) {
          if (response.data && response.data.branches && Array.isArray(response.data.branches)) {
            branchesData = response.data.branches
          } else if (Array.isArray(response.data)) {
            branchesData = response.data
          }
        }
        
        let activeBranches = branchesData.filter(branch => branch.isActive)
        
        // Filter to only user's branch if restricted
        if (isBranchRestricted && userBranchId) {
          activeBranches = activeBranches.filter(branch => branch._id === userBranchId)
          console.log('🔒 Raw Material Report: Filtered to user\'s branch only')
        }
        
        setBranches(activeBranches)
      } catch (error) {
        console.error('Error loading branches:', error)
        setBranches([
          { _id: '609f1b8c8d2e4a1b2c3d4e5f', name: 'Main Branch', type: 'outlet' },
          { _id: '609f1b8c8d2e4a1b2c3d4e60', name: 'Downtown Branch', type: 'outlet' }
        ])
      } finally {
        setLoading(false)
      }
    }
    
    loadBranches()
  }, [])

  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    const clearedFilters = {
      branch: isBranchRestricted ? userBranchId : ''
    }
    onFiltersChange(clearedFilters)
    onClear()
  }

  const hasActiveFilters = useMemo(() => {
    // Don't show clear button if only filter is restricted user's own branch
    return filters?.branch && (!isBranchRestricted || filters.branch !== userBranchId)
  }, [filters, isBranchRestricted, userBranchId])

  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Branch {isBranchRestricted && <span className="text-xs text-gray-500">(locked to your branch)</span>}
          </label>
          <div className="relative">
            <select
              value={filters?.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10 appearance-none ${
                isBranchRestricted ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              disabled={loading || isBranchRestricted}
            >
              <option value="">All Branches</option>
              {branches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  )
}

const RawMaterialSummaryReport = () => {
  const columns = useMemo(() => [
    { 
      key: 'code', 
      title: 'Code', 
      sortable: true
    },
    { 
      key: 'materialName', 
      title: 'Material Name', 
      sortable: true
    },
    { 
      key: 'currentStockWithUnit', 
      title: 'Current Stock', 
      sortable: true,
      render: (value) => {
        return value || '0'
      }
    },
    { 
      key: 'branchName', 
      title: 'Branch Name', 
      sortable: true
    }
  ], [])

  return (
    <BaseReportPage
      title="Raw Material Summary Report"
      subtitle="View current stock levels of raw materials by branch"
      reportType="rawMaterialSummary"
      columns={columns}
      customFilterComponent={RawMaterialFilters}
      enableFilters={true}
      fileName="raw-material-summary"
    />
  )
}

export default RawMaterialSummaryReport
